import {
  Id,
  Identifiable,
  Resource,
  resource,
  resourcesListingFilter,
} from "@planzone/dto";
import { Repository } from "../../adapters/repository";
import { present } from "../../../utils/presenter";
import { Interactor } from "./types";

const BATCH_DELAY = 50;
const BATCH_SIZE = 200;

type Batch = { uuids: Set<Id>; resources: Promise<Identifiable<Resource>[]> };

// The values shown at once (the rows of a list) ask their uuids within a few milliseconds: the requests are
// gathered, then the listing restricted to their uuids is read once per batch of uuids.
function createInteractor(repository: Repository): Interactor {
  let batch: Batch | null = null;

  const retrieve = (uuids: Id[]) =>
    repository
      .getPlanzoneResourcesListing({
        ...resourcesListingFilter,
        size: uuids.length,
        resources: uuids.map((uuid) => ({ ...resource, uuid })),
      })
      .then(({ resources }) => resources);

  const join = (uuids: Id[]) => {
    if (!batch) {
      const next: Batch = { uuids: new Set(), resources: Promise.resolve([]) };

      next.resources = new Promise((resolve) =>
        setTimeout(resolve, BATCH_DELAY),
      ).then(() => {
        const requested = [...next.uuids];

        batch = null;

        return Promise.all(
          Array.from(
            { length: Math.ceil(requested.length / BATCH_SIZE) },
            (_, index) =>
              retrieve(
                requested.slice(index * BATCH_SIZE, (index + 1) * BATCH_SIZE),
              ),
          ),
        ).then((pages) => pages.flat());
      });
      batch = next;
    }

    uuids.forEach((uuid) => batch?.uuids.add(uuid));

    return batch.resources;
  };

  return ({ uuids }) =>
    present(
      join(uuids)
        .then((resources) => ({
          uuids,
          resources: resources.filter(({ uuid }) => uuids.includes(uuid)),
        }))
        .catch(() => Promise.reject({ uuids })),
    );
}

export type { Interactor };

export { createInteractor };
